import React from "react";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Checkbox } from "@outpave/ds";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { Stack } from "components/layout/reusableLayouts";
import { formatCashFull } from "utils/formatCash";

interface MandateConsentProps {
  companyName: string;
  amount: number;
  currency: string;
  intervalLabel: string;
  maxPayments?: number;
  endsAt?: number;
  accepted: boolean;
  onChange: (accepted: boolean) => void;
}

/* Recurring-payment consent must remain separate from general terms. */
const MandateConsent: React.FC<MandateConsentProps> = ({
  companyName,
  amount,
  currency,
  intervalLabel,
  maxPayments,
  endsAt,
  accepted,
  onChange,
}) => {
  const { t } = useTranslation();

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack sx={{ gap: "var(--space-2)" }}>
          <Typography variant="body2">
            {t(termKey(maxPayments, endsAt), {
              companyName,
              amount: formatCashFull(amount, currency),
              interval: intervalLabel,
              count: maxPayments,
              date: endsAt
                ? new Date(endsAt).toLocaleDateString(undefined, {
                    timeZone: "UTC",
                  })
                : "",
            })}
          </Typography>

          <Typography
            variant="caption"
            sx={{ color: "var(--color-text-secondary)" }}
          >
            {t("payLink.MandateCancelAnytime")}
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={accepted}
                onChange={(event) => onChange(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2">
                {t("payLink.MandateAccept")}
              </Typography>
            }
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

const termKey = (maxPayments?: number, endsAt?: number): string => {
  if (maxPayments && endsAt) return "payLink.MandateTermsForCountUntilDate";
  if (maxPayments) return "payLink.MandateTermsForCount";
  if (endsAt) return "payLink.MandateTermsUntilDate";

  return "payLink.MandateTerms";
};

export default MandateConsent;
